'use client';

import { c as _c } from "react/compiler-runtime";
import { type RouterOutputs } from '@shared/all/domains/trpc';
import { type PostFeedInput } from '@shared/all/validators';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import useLocalPreference from '@shared/utils/hooks/useLocalPreference';
import useNavigator from '@shared/utils/hooks/useNavigator';
import usePostsShared from '@shared/utils/hooks/usePosts';
import useUser from '@shared/utils/hooks/useUser';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { client } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import Error from '../common/Error';
import PostMode from './PostMode';
type Props = {
  params: PostFeedInput;
  uniqueId?: string;
  manualLoading?: boolean;
  hideEndReachedMessage?: boolean;
};
type FactiiiGroup = RouterOutputs['posts']['getRelatedFactiiiCountGroups']['items'][number];
const SortByFactiiiMode = props => {
  const $ = _c(14);
  const {
    localPreferenceState,
    getPostParams
  } = useLocalPreference();
  const {
    currentUser
  } = useUser();
  const [expandedGroup, setExpandedGroup] = useState();
  const {
    queryKey
  } = usePostsShared(client, "RelatedFactiiiCountGroups");
  const {
    navigatorState
  } = useNavigator();
  let t0;
  if ($[0] !== getPostParams || $[1] !== localPreferenceState || $[2] !== props.params) {
    t0 = async t1 => {
      const {
        pageParam
      } = t1;
      if (localPreferenceState.postModeType === "private") {
        return {
          items: []
        };
      }
      const result = await unwrapFromResult(client.posts.getRelatedFactiiiCountGroups.query(getPostParams(props.params, pageParam, undefined)));
      return {
        items: result.items
      };
    };
    $[0] = getPostParams;
    $[1] = localPreferenceState;
    $[2] = props.params;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const t1 = currentUser.isAuthenticated !== null;
  let t2;
  if ($[4] !== queryKey || $[5] !== t0 || $[6] !== t1) {
    t2 = {
      queryKey,
      queryFn: t0,
      initialPageParam: undefined as number | undefined,
      getNextPageParam: _temp,
      refetchOnWindowFocus: false,
      enabled: t1
    };
    $[4] = queryKey;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage
  } = useInfiniteQuery(t2);
  const allGroups = data?.pages.flatMap(_temp2) || [];
  const virtualizer = useWindowVirtualizer({
    count: allGroups.length,
    estimateSize: _temp3,
    overscan: 5
  });
  const virtualItems = virtualizer.getVirtualItems();
  const lastVirtualItem = virtualItems.at(-1);
  useEffect(() => {
    if (lastVirtualItem && lastVirtualItem.index >= allGroups.length - 3 && !isFetching && !isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [virtualItems, lastVirtualItem, allGroups.length, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage]);
  const formatGroupName = _temp5;
  let t3;
  if ($[8] !== error || $[9] !== navigatorState) {
    t3 = error && navigatorState.isOnline && <Error error={error} loading={false} message="Something went wrong while loading content! Please try again by refreshing." />;
    $[8] = error;
    $[9] = navigatorState;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const t4 = (!error || !navigatorState.isOnline) && <><div style={{
      height: `${virtualizer.getTotalSize()}px`,
      width: "100%",
      position: "relative"
    }}>{virtualItems.map(virtualItem => {
        const group_0 = allGroups[virtualItem.index];
        if (!group_0) {
          return null;
        }
        const groupKey = group_0.factiiis.map(_temp6).join(",");
        const isExpanded = expandedGroup === groupKey ? true : false;
        const groupName = formatGroupName(group_0);
        return <div key={virtualItem.key} data-index={virtualItem.index} ref={virtualizer.measureElement} style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          transform: `translateY(${virtualItem.start}px)`
        }}><div className="mb-4 rounded-md bg-background-secondary shadow-lg"><div className="z-50 flex items-center justify-between rounded-md border-2 border-ds-border-standard bg-background-primary p-4"><div className="flex items-center"><button className="mr-2 p-1 hover:text-primary" onClick={() => isExpanded ? setExpandedGroup(undefined) : setExpandedGroup(groupKey)}>{isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}</button><div className="ml-2 flex flex-col"><div className="flex items-center gap-2"><div className="flex -space-x-1.5">{group_0.factiiis.map(_temp7)}</div><Link href={`/fids/${groupKey}`} className="font-semibold leading-none">{groupName}</Link></div><div className="mt-1 text-sm text-textSecondary/70">{group_0.count}{" "}{group_0.count === 1 ? "co-occurrence" : "co-occurrences"}</div></div></div></div>{isExpanded && <div className="px-4"><PostMode params={props.params} factiiiIds={group_0.factiiis.map(_temp8)} isFullLength={false} disableLink={false} hideEndReachedMessage={true} uniqueId={props.uniqueId ? `${props.uniqueId}-${groupKey}` : groupKey} useContainerVirtualizer={true} /></div>}</div></div>;
      })}</div>{!props.hideEndReachedMessage && !hasNextPage && !isFetching && <div className="flex w-full flex-col items-center space-y-2 p-4 text-center text-textSecondary/70"><CheckCircle2 /><p className="text-textSecondary/70">You're all caught up.</p></div>}{!props.manualLoading && hasNextPage && <div className="flex flex-col justify-center space-y-4"><div className="loader" /></div>}{props.manualLoading && <div className="flex items-center gap-x-2 p-4"><div className="flex items-center space-x-1 font-semibold"><p className="mt-1">{allGroups.length || 0}</p><p>{allGroups.length === 1 ? "group" : "groups"} loaded</p></div>{hasNextPage && <>•<Button text={isFetchingNextPage ? "" : "Load More"} variant="SECONDARY" loading={isFetchingNextPage} onClick={() => void fetchNextPage()} disabled={!hasNextPage || isFetchingNextPage || isFetching} classNames="!p-0 hover:bg-transparent !text-primary hover:underline" /></>}</div>}</>;
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
export default SortByFactiiiMode;
function _temp(lastPage) {
  const items = lastPage.items;
  if (items.length < 10) {
    return;
  }
  return items[items.length - 1]?.id;
}
function _temp2(page) {
  return page.items;
}
function _temp3() {
  return 200;
}
function _temp4(f) {
  return f.name;
}
function _temp5(group) {
  return group.factiiis.map(_temp4).join(",");
}
function _temp6(f_0) {
  return f_0.id;
}
function _temp7(factiii, index) {
  return <Avatar key={factiii.id} factiiiAvatarKey={factiii.avatarKey} factiiiId={factiii.id} size={20} className={`ring-1 ring-background-secondary ${index > 0 ? "z-10" : ""}`} />;
}
function _temp8(f_1) {
  return f_1.id;
}